/**
 * useFinance — TanStack Query data layer for the finance module.
 *
 * Reads go through the `finance_entries_with_status` view so `computed_status`
 * (overdue detection) comes from the server without a write. The full set of a
 * user's entries is fetched once and cached (volume is low — manual entries);
 * range/kind/status filters and all aggregation happen client-side so the whole
 * module keeps working offline from the last-known-good cache.
 *
 * Offline mutations are delegated to useOfflineSync, which enqueues the op and
 * updates the local cache. See [[orcamigo-offline-sync-uuid-bug]] for why we
 * never let an offline_ id reach Postgres.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addMonths, addWeeks, addYears, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import {
  cacheFinanceCategories,
  cacheFinanceEntries,
  getCachedFinanceCategories,
  getCachedFinanceEntries,
} from '@/lib/offlineDataCache';
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';
import {
  BillableExpenseError,
  billableOfflineError,
  removeBillableQuoteLine,
} from '@/lib/billableExpenseSync';
import { notifyBillableExpensesUpdated } from '@/lib/billableExpense';
import type {
  FinanceCategory,
  FinanceEntryWithStatus,
  FinanceKind,
  FinanceStatus,
  PaymentMethod,
  Recurrence,
} from '@/types/database';
import { toast } from 'sonner';

const ENTRIES_KEY = 'finance-entries';
const CATEGORIES_KEY = 'finance-categories';

/**
 * Nome do evento que anuncia mudança em `finance_entries` para quem NÃO usa a
 * query deste módulo.
 *
 * Existe porque a trilha do trabalho (`useWork`) prova a etapa de pagamento a
 * partir do financeiro, mas tem query própria: sem aviso, registrar um
 * recebimento pelo `QuoteFinancePrompt` deixava a etapa de pagamento vazia na
 * MESMA tela em que o usuário acabou de registrar — o estado anterior à própria
 * ação dele (#80). Invalidar a query daqui não alcança a de lá.
 *
 * Guard de varredura em `src/test/financeChangeEvent.test.ts`: toda invalidação
 * de `ENTRIES_KEY` tem que vir acompanhada do aviso, senão uma mutation nova
 * volta a deixar a trilha parada sem erro nenhum (modo de falha do #89).
 */
export const FINANCE_ENTRIES_CHANGED_EVENT = 'finance-entries-updated';

export function notifyFinanceEntriesChanged(): void {
  window.dispatchEvent(new CustomEvent(FINANCE_ENTRIES_CHANGED_EVENT));
}

/** How many occurrences a recurring entry expands into (parent + children). */
export const RECURRENCE_OCCURRENCES = 12;

export interface DefaultCategorySeed {
  name: string;
  kind: FinanceKind;
  color: string;
  icon: string;
}

export const DEFAULT_FINANCE_CATEGORIES: DefaultCategorySeed[] = [
  { name: 'Vendas', kind: 'income', color: '#10b981', icon: 'ShoppingCart' },
  { name: 'Serviços', kind: 'income', color: '#0ea5e9', icon: 'Wrench' },
  { name: 'Reembolsos', kind: 'income', color: '#8b5cf6', icon: 'Undo2' },
  { name: 'Fornecedores', kind: 'expense', color: '#ef4444', icon: 'Truck' },
  { name: 'Impostos', kind: 'expense', color: '#f59e0b', icon: 'Landmark' },
  { name: 'Aluguel', kind: 'expense', color: '#6366f1', icon: 'Building2' },
  { name: 'Salários', kind: 'expense', color: '#ec4899', icon: 'Users' },
  { name: 'Materiais', kind: 'expense', color: '#14b8a6', icon: 'Package' },
  { name: 'Marketing', kind: 'expense', color: '#f97316', icon: 'Megaphone' },
  { name: 'Outros', kind: 'expense', color: '#64748b', icon: 'MoreHorizontal' },
];

export interface FinanceEntryInput {
  kind: FinanceKind;
  description: string;
  amount: number;
  due_date: string; // 'YYYY-MM-DD'
  paid_date?: string | null;
  status?: FinanceStatus;
  payment_method?: PaymentMethod | null;
  recurrence?: Recurrence | null;
  category_id?: string | null;
  client_id?: string | null;
  quote_id?: string | null;
  company_id?: string | null;
  notes?: string | null;
  /**
   * Despesa COBRÁVEL: repassada ao cliente. Materializa uma linha em
   * `quote_items` do `quote_id` vinculado (o banco tem CHECK exigindo
   * `kind: 'expense'` + `quote_id`). Ver `src/lib/billableExpense.ts`.
   */
  billable?: boolean;
  /** Comprovante anexo (objeto do bucket PRIVADO; ler via `resolveMediaUrl`). */
  attachment_url?: string | null;
}

export type DeleteScope = 'single' | 'future';

// ===== Recurrence helpers =====

function advanceDate(dateStr: string, recurrence: Recurrence, steps: number): string {
  const base = parseISO(dateStr);
  let next: Date;
  switch (recurrence) {
    case 'weekly':
      next = addWeeks(base, steps);
      break;
    case 'yearly':
      next = addYears(base, steps);
      break;
    case 'monthly':
    default:
      next = addMonths(base, steps);
      break;
  }
  return todayIsoDate(next);
}

/**
 * Expand an input into the list of rows to persist. A non-recurring entry
 * yields a single row; a recurring one yields RECURRENCE_OCCURRENCES rows with
 * advancing due dates. The parent index is 0 (recurrence_parent_id linkage is
 * applied by the caller once the parent id is known).
 */
export function expandRecurrence(input: FinanceEntryInput): FinanceEntryInput[] {
  const recurrence = input.recurrence;
  if (!recurrence || recurrence === 'none') {
    return [input];
  }
  return Array.from({ length: RECURRENCE_OCCURRENCES }, (_, i) => ({
    ...input,
    due_date: i === 0 ? input.due_date : advanceDate(input.due_date, recurrence, i),
  }));
}

// ===== Categories =====

export function useFinanceCategories() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [CATEGORIES_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 5 * 60 * 1000,
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedFinanceCategories(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<FinanceCategory[]> => {
      if (!user?.id || !companyId) return [];

      const { data, error } = await supabase
        .from('finance_categories')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      let categories = (data ?? []) as FinanceCategory[];

      // Seed the default set on first access (per company).
      if (categories.length === 0) {
        const seed = DEFAULT_FINANCE_CATEGORIES.map((c) => ({
          ...c,
          user_id: user.id,
          company_id: companyId,
        }));
        const { data: seeded, error: seedError } = await supabase
          .from('finance_categories')
          .insert(seed)
          .select('*');
        if (seedError) {
          // Non-fatal: another device may have seeded concurrently. Re-read.
          const { data: reread } = await supabase
            .from('finance_categories')
            .select('*')
            .eq('company_id', companyId)
            .order('created_at', { ascending: true });
          categories = (reread ?? []) as FinanceCategory[];
        } else {
          categories = (seeded ?? []) as FinanceCategory[];
        }
      }

      cacheFinanceCategories(user.id, categories);
      return categories;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY, companyId] });
  }, [queryClient, companyId]);

  return { ...query, categories: query.data ?? [], invalidate };
}

export function useCreateFinanceCategory() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { name: string; kind: FinanceKind; color?: string; icon?: string | null }) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      const { data, error } = await supabase
        .from('finance_categories')
        .insert({
          user_id: user.id,
          company_id: companyId,
          name: input.name.trim(),
          kind: input.kind,
          color: input.color ?? '#10b981',
          icon: input.icon ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as FinanceCategory;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY, companyId] }),
    onError: () => toast.error('Não foi possível salvar a categoria.'),
  });
}

export function useUpdateFinanceCategory() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      updates: Partial<Pick<FinanceCategory, 'name' | 'kind' | 'color' | 'icon'>>;
    }) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      // Scope by company (not user) so admins can manage the owner's categories.
      const { error } = await supabase
        .from('finance_categories')
        .update(input.updates)
        .eq('id', input.id)
        .eq('company_id', companyId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY, companyId] }),
    onError: () => toast.error('Não foi possível atualizar a categoria.'),
  });
}

export function useDeleteFinanceCategory() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      const { error } = await supabase
        .from('finance_categories')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CATEGORIES_KEY, companyId] }),
    onError: () => toast.error('Não foi possível excluir a categoria.'),
  });
}

// ===== Entries (raw + filtered) =====

/**
 * Base query: every entry for the user, from the computed-status view. All
 * other entry hooks derive from this so filtering/aggregation stays client-side
 * and offline-capable.
 */
export function useFinanceEntriesRaw() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [ENTRIES_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    // `initialData` + `staleTime` fazem o TanStack tratar o cache do disco como
    // FRESCO e pular o refetch ao montar (armadilha #11). Medido em device real:
    // a tela do orçamento recarrega depois de lançar uma despesa cobrável, o
    // componente remonta, e a lista de despesas voltava VAZIA — o lançamento
    // existia no banco e na tela do financeiro, mas não ali.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedFinanceEntries(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<FinanceEntryWithStatus[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('finance_entries_with_status')
        .select('*')
        .eq('company_id', companyId)
        .order('due_date', { ascending: false });
      if (error) throw error;
      const entries = (data ?? []) as FinanceEntryWithStatus[];
      cacheFinanceEntries(user.id, entries);
      return entries;
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, companyId] });
      notifyFinanceEntriesChanged();
  }, [queryClient, companyId]);

  /** Push the current local cache into the query (used after offline writes). */
  const syncFromCache = useCallback(() => {
    if (!user?.id) return;
    queryClient.setQueryData([ENTRIES_KEY, companyId], getCachedFinanceEntries(user.id) ?? []);
  }, [queryClient, user?.id, companyId]);

  return { ...query, entries: query.data ?? [], invalidate, syncFromCache };
}

function inRange(entry: FinanceEntryWithStatus, from?: string, to?: string): boolean {
  if (from && entry.due_date < from) return false;
  if (to && entry.due_date > to) return false;
  return true;
}

/**
 * `useFinanceEntries` (recorte por período/tipo/status) foi REMOVIDO quando
 * `/financeiro` ganhou o desenho oficial: o hero soma exatamente o que a lista
 * mostra, então o recorte subiu para a página e passou a sair de um módulo puro
 * e testado (`src/lib/financeListFilters.ts`). Deixá-lo aqui sem consumidor o
 * tornaria uma SEGUNDA implementação da mesma regra — inclusive do status
 * efetivo, que ele lia como `computed_status ?? status` — pronta para divergir
 * na primeira correção (#187). `inRange` continua, porque
 * `aggregateFinanceSummary` (abaixo) o usa.
 */

export interface FinanceSummary {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  pendingIncome: number;
  overdueExpense: number;
  byCategory: { id: string | null; name: string; total: number; color: string }[];
  byMonth: { month: string; income: number; expense: number }[];
}

/**
 * Pure aggregation over entries — exported so it can be unit-tested without
 * rendering. Income/expense totals, pending income, overdue expense, expenses
 * by category (for the pie) and income/expense by month (for the bar chart).
 */
export function aggregateFinanceSummary(
  entries: FinanceEntryWithStatus[],
  categories: Pick<FinanceCategory, 'id' | 'name' | 'color'>[] = [],
  range: { from?: string; to?: string } = {},
): FinanceSummary {
  const { from, to } = range;
  const scoped = entries.filter((e) => inRange(e, from, to));

  let totalIncome = 0;
  let totalExpense = 0;
  let pendingIncome = 0;
  let overdueExpense = 0;

  const categoryName = new Map<string, { name: string; color: string }>();
  categories.forEach((c) => categoryName.set(c.id, { name: c.name, color: c.color ?? '#64748b' }));

  const byCategoryMap = new Map<string, { name: string; total: number; color: string }>();
  const byMonthMap = new Map<string, { income: number; expense: number }>();

  for (const e of scoped) {
    const amount = Number(e.amount ?? 0);
    const cstatus = (e.computed_status ?? e.status) as FinanceStatus;
    const paid = e.status === 'paid';

    if (e.kind === 'income') {
      totalIncome += amount;
      if (!paid && cstatus !== 'canceled') pendingIncome += amount;
    } else {
      totalExpense += amount;
      if (cstatus === 'overdue') overdueExpense += amount;

      // Expense-by-category only (matches the Visão Geral pie).
      const key = e.category_id ?? '__none__';
      const meta = e.category_id ? categoryName.get(e.category_id) : undefined;
      const prev = byCategoryMap.get(key) ?? {
        name: meta?.name ?? 'Sem categoria',
        total: 0,
        color: meta?.color ?? '#64748b',
      };
      prev.total += amount;
      byCategoryMap.set(key, prev);
    }

    const month = (e.due_date ?? '').slice(0, 7); // 'YYYY-MM'
    const m = byMonthMap.get(month) ?? { income: 0, expense: 0 };
    if (e.kind === 'income') m.income += amount;
    else m.expense += amount;
    byMonthMap.set(month, m);
  }

  const byCategory = Array.from(byCategoryMap.entries())
    .map(([id, v]) => ({ id: id === '__none__' ? null : id, name: v.name, total: v.total, color: v.color }))
    .sort((a, b) => b.total - a.total);

  const byMonth = Array.from(byMonthMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, v]) => ({ month, income: v.income, expense: v.expense }));

  return {
    totalIncome,
    totalExpense,
    balance: totalIncome - totalExpense,
    pendingIncome,
    overdueExpense,
    byCategory,
    byMonth,
  };
}

export function useFinanceSummary(
  range: { from?: string; to?: string } = {},
): { summary: FinanceSummary; isLoading: boolean } {
  const { entries, isLoading } = useFinanceEntriesRaw();
  const { categories } = useFinanceCategories();
  const { from, to } = range;

  const summary = useMemo<FinanceSummary>(
    () => aggregateFinanceSummary(entries, categories, { from, to }),
    [entries, categories, from, to],
  );

  return { summary, isLoading };
}

// ===== Entry mutations =====

export function useCreateFinanceEntry() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveFinanceEntryOffline } = useOfflineSync();

  const refresh = useCallback(
    (online: boolean) => {
      if (!user?.id) return;
      if (online) {
        queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, companyId] });
      notifyFinanceEntriesChanged();
      } else {
        queryClient.setQueryData([ENTRIES_KEY, companyId], getCachedFinanceEntries(user.id) ?? []);
      }
    },
    [queryClient, user?.id, companyId],
  );

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: FinanceEntryInput) => {
      if (!user?.id) throw new Error('Sem sessão');

      // Default to the active company so entries land in the right scope even
      // if the caller didn't set it explicitly.
      const entryCompanyId = input.company_id ?? companyId;
      const rows = expandRecurrence({ ...input, company_id: entryCompanyId });
      const online = await checkNetworkStatus();

      if (!online) {
        // Enqueue the parent first so children can reference its offline id.
        const parentOfflineId = saveFinanceEntryOffline({
          entry: { ...rows[0], user_id: user.id, company_id: entryCompanyId },
          userId: user.id,
        });
        for (let i = 1; i < rows.length; i++) {
          saveFinanceEntryOffline({
            entry: { ...rows[i], user_id: user.id, company_id: entryCompanyId, recurrence_parent_id: parentOfflineId },
            userId: user.id,
          });
        }
        refresh(false);
        return { offline: true as const };
      }

      // Online: insert parent, then link children to its real id.
      const toPayload = (row: FinanceEntryInput, parentId: string | null) => ({
        user_id: user.id,
        kind: row.kind,
        description: row.description.trim(),
        amount: Number(row.amount),
        due_date: row.due_date,
        paid_date: row.paid_date ?? null,
        status: row.status ?? 'pending',
        payment_method: row.payment_method ?? null,
        recurrence: row.recurrence ?? null,
        recurrence_parent_id: parentId,
        category_id: row.category_id ?? null,
        client_id: row.client_id ?? null,
        quote_id: row.quote_id ?? null,
        company_id: row.company_id ?? entryCompanyId,
        notes: row.notes ?? null,
        // Colunas do grupo 5. Este payload é montado por LISTA EXPLÍCITA de
        // colunas (armadilha #33/#62): esquecer uma delas grava o lançamento com
        // sucesso, sem erro de RLS, e o campo simplesmente não chega ao banco.
        billable: row.billable ?? false,
        attachment_url: row.attachment_url ?? null,
      });

      const { data: parent, error: parentError } = await supabase
        .from('finance_entries')
        .insert(toPayload(rows[0], null))
        .select('*')
        .single();
      if (parentError) throw parentError;

      if (rows.length > 1) {
        const childPayloads = rows.slice(1).map((r) => toPayload(r, (parent as any).id));
        const { error: childError } = await supabase.from('finance_entries').insert(childPayloads);
        if (childError) throw childError;
      }

      refresh(true);
      return { offline: false as const, id: (parent as any).id as string };
    },
    onError: () => toast.error('Não foi possível salvar o lançamento.'),
  });
}

export function useUpdateFinanceEntry() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { updateFinanceEntryOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; updates: Partial<FinanceEntryInput> }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const online = await checkNetworkStatus();

      if (!online) {
        updateFinanceEntryOffline({ entryId: input.id, updates: input.updates, userId: user.id });
        queryClient.setQueryData([ENTRIES_KEY, companyId], getCachedFinanceEntries(user.id) ?? []);
        return;
      }

      // Scope by company (not user) so admins can edit the company's entries.
      let query = supabase
        .from('finance_entries')
        .update(input.updates)
        .eq('id', input.id);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, companyId] });
      notifyFinanceEntriesChanged();
    },
    onError: () => toast.error('Não foi possível atualizar o lançamento.'),
  });
}

export function useMarkPaid() {
  const update = useUpdateFinanceEntry();
  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; paidDate?: string }) => {
      await update.mutateAsync({
        id: input.id,
        updates: { status: 'paid', paid_date: input.paidDate ?? todayIsoDate() },
      });
    },
    onError: () => toast.error('Não foi possível marcar como pago.'),
  });
}

export function useDeleteFinanceEntry() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { entries } = useFinanceEntriesRaw();
  const { deleteFinanceEntryOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: { id: string; scope?: DeleteScope }) => {
      if (!user?.id) throw new Error('Sem sessão');
      const scope = input.scope ?? 'single';
      const online = await checkNetworkStatus();

      // Resolve the target ids: a single entry, or the whole future tail of a
      // recurrence series (this occurrence and every later one).
      let targetIds = [input.id];
      if (scope === 'future') {
        const target = entries.find((e) => e.id === input.id);
        if (target) {
          const seriesRoot = target.recurrence_parent_id ?? target.id;
          targetIds = entries
            .filter(
              (e) =>
                (e.id === seriesRoot || e.recurrence_parent_id === seriesRoot) &&
                (e.due_date ?? '') >= (target.due_date ?? ''),
            )
            .map((e) => e.id);
          if (targetIds.length === 0) targetIds = [input.id];
        }
      }

      // Despesas COBRÁVEIS cobram um valor num orçamento. Excluir só o
      // lançamento deixaria a linha materializada cobrando um valor que o
      // financeiro não conhece mais — e o `ON DELETE SET NULL` da FK a
      // transformaria numa linha órfã e intocável. Então o vínculo é desfeito
      // primeiro: linha fora, `quotes.total` recalculado.
      const billableTargets = targetIds
        .map((id) => entries.find((e) => e.id === id))
        .filter((e): e is FinanceEntryWithStatus => Boolean(e?.billable && e?.quote_id));

      if (billableTargets.length > 0) {
        // Sem rede não dá para desfazer o vínculo (nem enfileirar: a fila não
        // recalcula total de documento). Recusar é melhor que excluir metade.
        if (!online) throw billableOfflineError();
        if (!companyId) throw new Error('Nenhuma empresa ativa');
        for (const target of billableTargets) {
          await removeBillableQuoteLine(target.id, target.quote_id!, companyId);
        }
        notifyBillableExpensesUpdated();
        window.dispatchEvent(new CustomEvent('quotes-updated'));
      }

      if (!online) {
        targetIds.forEach((id) => deleteFinanceEntryOffline({ entryId: id, userId: user.id }));
        queryClient.setQueryData([ENTRIES_KEY, companyId], getCachedFinanceEntries(user.id) ?? []);
        return;
      }

      let query = supabase
        .from('finance_entries')
        .delete()
        .in('id', targetIds);
      if (companyId) query = query.eq('company_id', companyId);
      const { error } = await query;
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, companyId] });
      notifyFinanceEntriesChanged();
    },
    onError: (error) => {
      // O erro da despesa cobrável já vem com mensagem pronta para o usuário
      // (orçamento aprovado, sem rede) — mostrá-lo é o que diferencia "não deu"
      // de "não pode".
      if (error instanceof BillableExpenseError) toast.warning(error.message);
      else toast.error('Não foi possível excluir o lançamento.');
    },
  });
}
